import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signupBrand, signupCreator, googleAuth } from '../api/api';
import {
  captureSignupCompleted,
  identifyBrandCreator,
  resetPosthog,
} from '../analytics/posthog';
import { MILAN_WORKSPACE_ENABLED, getPostAuthPath } from '../config/productFlags';
import './Auth.css';

function Signup() {
  const navigate = useNavigate();
  const emailRef = useRef(null);
  const [userType, setUserType] = useState('brand');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    contact_name: '',
    industry: '',
    website: '',
    name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showGoogleForm, setShowGoogleForm] = useState(false);
  const [googleToken, setGoogleToken] = useState(null);

  // Refs so Google callback stays stable — otherwise useEffect re-runs on every form
  // keystroke, removes the GSI script, and breaks Sign-In (local + prod).
  const userTypeRef = useRef(userType);
  const formDataRef = useRef(formData);
  useEffect(() => {
    userTypeRef.current = userType;
  }, [userType]);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // Google OAuth Handler (stable identity — reads latest form via refs)
  const handleGoogleSignup = useCallback(async (response) => {
    if (!response.credential && !response.access_token) {
      setError('Google authentication failed');
      return;
    }

    const token = response.credential || response.access_token;
    const ut = userTypeRef.current;
    const fd = formDataRef.current;
    setGoogleLoading(true);
    setError('');

    try {
      const additionalData = {};
      
      if (ut === 'brand') {
        // For brands, we need company_name and industry
        if (!fd.company_name || !fd.industry) {
          setGoogleToken(token);
          setShowGoogleForm(true);
          setGoogleLoading(false);
          return;
        }
        additionalData.company_name = fd.company_name;
        additionalData.industry = fd.industry;
      } else {
        // For creators, name is optional (Google provides it)
        if (fd.name) {
          additionalData.name = fd.name;
        }
      }

      const res = await googleAuth(token, ut, additionalData);
      const { access_token, user_id, user_type, user_name, email } = res.data;

      // Store auth data
      localStorage.setItem('token', access_token);
      localStorage.setItem('userEmail', email);
      localStorage.setItem('userName', user_name || '');
      localStorage.setItem('userType', user_type);
      localStorage.setItem('userId', user_id);

      resetPosthog();
      identifyBrandCreator(user_id, user_type);
      captureSignupCompleted('google', user_type === 'creator' ? 'creator' : 'brand');

      // Navigate to appropriate dashboard
      navigate(getPostAuthPath(user_type));
    } catch (err) {
      if (err.response?.data?.detail === 'new_user_brand') {
        // Need more info for brand signup
        setGoogleToken(token);
        setShowGoogleForm(true);
      } else {
        setError(err.response?.data?.detail || 'Google signup failed');
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [navigate]);

  const googleSignupCbRef = useRef(handleGoogleSignup);
  googleSignupCbRef.current = handleGoogleSignup;

  const googleTokenClientRef = useRef(null);

  // Complete Google signup for brands (after filling company info)
  const completeGoogleSignup = async (e) => {
    e.preventDefault();
    if (!googleToken) return;

    setGoogleLoading(true);
    setError('');

    try {
      const res = await googleAuth(googleToken, 'brand', {
        company_name: formData.company_name,
        industry: formData.industry
      });
      
      const { access_token, user_id, user_type, user_name, email } = res.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('userEmail', email);
      localStorage.setItem('userName', user_name || formData.company_name || '');
      localStorage.setItem('userType', user_type);
      localStorage.setItem('userId', user_id);

      resetPosthog();
      identifyBrandCreator(user_id, user_type);
      captureSignupCompleted('google', 'brand');

      navigate(getPostAuthPath(user_type));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete signup');
    } finally {
      setGoogleLoading(false);
    }
  };

  // OAuth token client for custom Google button (see Login.js — One Tap prompt is unreliable here).
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn('Google Client ID not configured');
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google?.accounts?.oauth2?.initTokenClient) {
        console.warn('Google OAuth2 token client unavailable');
        return;
      }
      googleTokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid email profile',
        callback: (tokenResponse) => {
          if (tokenResponse.error) {
            setGoogleLoading(false);
            const quiet = ['popup_closed_by_user', 'access_denied', 'user_cancelled'].includes(
              String(tokenResponse.error)
            );
            if (!quiet) {
              setError(
                tokenResponse.error_description ||
                  tokenResponse.error ||
                  'Google sign-in failed'
              );
            }
            return;
          }
          if (tokenResponse.access_token) {
            googleSignupCbRef.current({ access_token: tokenResponse.access_token });
          } else {
            setGoogleLoading(false);
          }
        },
      });
    };
    document.body.appendChild(script);

    return () => {
      googleTokenClientRef.current = null;
      const existingScript = document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]'
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const triggerGoogleSignIn = () => {
    if (!googleTokenClientRef.current) {
      setError('Google Sign-In not loaded. Please refresh the page.');
      return;
    }
    setError('');
    setGoogleLoading(true);
    googleTokenClientRef.current.requestAccessToken();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      let response;
      if (userType === 'brand') {
        const brandData = {
          email: formData.email,
          password: formData.password,
          company_name: formData.company_name,
          contact_name: formData.contact_name,
          industry: formData.industry
        };
        if (formData.website) {
          brandData.website = formData.website;
        }
        response = await signupBrand(brandData);
      } else {
        const creatorData = {
          email: formData.email,
          password: formData.password,
          name: formData.name
        };
        
        response = await signupCreator(creatorData);
      }
      
      // Save login info from signup response for auto-login
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('userEmail', response.data.email || formData.email);
        localStorage.setItem('userId', response.data.user_id);
        localStorage.setItem('userType', response.data.user_type || userType);
        localStorage.setItem('userName', response.data.user_name || formData.name || formData.company_name || '');
        resetPosthog();
        identifyBrandCreator(response.data.user_id, response.data.user_type || userType);
        captureSignupCompleted('email', userType);
      }
      
      navigate('/verify-email', { state: { email: formData.email, message: 'Account created successfully! Please check your email to verify your account.', userType: userType } });
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response.data.detail || 'Account already exists');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const industries = [
    'Technology',
    'Fashion & Apparel',
    'Beauty & Cosmetics',
    'Food & Beverage',
    'Health & Fitness',
    'Travel & Tourism',
    'Finance & Banking',
    'Education',
    'Entertainment',
    'E-commerce',
    'Real Estate',
    'Other'
  ];

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-left">
          <Link to="/" className="auth-logo">
            <img src="/logo.png" alt="Milan AI" className="logo-image" />
            <span className="logo-text">Milan AI</span>
          </Link>
          
          <div className="auth-content">
            <h1>Create Account</h1>
            <p>Join Milan AI and start collaborating today</p>

            <div className="user-type-toggle">
              <button 
                className={`type-btn ${userType === 'brand' ? 'active' : ''}`}
                onClick={() => setUserType('brand')}
                type="button"
              >
                <span className="type-icon">🏢</span>
                I'm a Brand
              </button>
              <button 
                className={`type-btn ${userType === 'creator' ? 'active' : ''}`}
                onClick={() => setUserType('creator')}
                type="button"
              >
                <span className="type-icon">🎨</span>
                I'm a Creator
              </button>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-group" onClick={() => emailRef.current?.focus()}>
                <label htmlFor="email" style={{ color: '#000000', fontWeight: 600 }}>Email Address</label>
                <input
                  ref={emailRef}
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={(e) => e.target.select()}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  autoFocus
                  style={{ color: '#000000' }}
                />
              </div>

              {userType === 'brand' ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="company_name" style={{ color: '#000000', fontWeight: 600 }}>Company Name</label>
                      <input
                        type="text"
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Your company name"
                        required
                        style={{ color: '#000000' }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="contact_name" style={{ color: '#000000', fontWeight: 600 }}>Your Name</label>
                      <input
                        type="text"
                        id="contact_name"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        required
                        style={{ color: '#000000' }}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="industry" style={{ color: '#000000', fontWeight: 600 }}>Industry</label>
                      <select
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        required
                        style={{ color: '#000000' }}
                      >
                        <option value="">Select industry</option>
                        {industries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="website" style={{ color: '#000000', fontWeight: 600 }}>Website <span className="optional-label">(Optional)</span></label>
                      <input
                        type="url"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        placeholder="https://yourcompany.com"
                        style={{ color: '#000000' }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label htmlFor="name" style={{ color: '#000000', fontWeight: 600 }}>Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                    style={{ color: '#000000' }}
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password" style={{ color: '#000000', fontWeight: 600 }}>Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min 6 characters"
                    required
                    style={{ color: '#000000' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword" style={{ color: '#000000', fontWeight: 600 }}>Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    required
                    style={{ color: '#000000' }}
                  />
                </div>
              </div>

              <label className="terms-checkbox">
                <input type="checkbox" required />
                <span>I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link></span>
              </label>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="auth-divider">
              <span>or sign up with</span>
            </div>

            <div className="social-auth">
              <button 
                className="social-btn google" 
                type="button"
                onClick={triggerGoogleSignIn}
                disabled={googleLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {googleLoading ? 'Connecting...' : 'Google'}
              </button>
            </div>

            {/* Google Brand Signup Modal */}
            {showGoogleForm && userType === 'brand' && (
              <div className="google-modal-overlay">
                <div className="google-modal">
                  <h3>Complete Your Brand Profile</h3>
                  <p>Just a few more details to get started</p>
                  <form onSubmit={completeGoogleSignup}>
                    <div className="form-group">
                      <label htmlFor="modal_company_name">Company Name</label>
                      <input
                        type="text"
                        id="modal_company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Your company name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="modal_industry">Industry</label>
                      <select
                        id="modal_industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select industry</option>
                        {industries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>
                    <div className="modal-actions">
                      <button 
                        type="button" 
                        className="btn-cancel"
                        onClick={() => {
                          setShowGoogleForm(false);
                          setGoogleToken(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={googleLoading}
                      >
                        {googleLoading ? 'Creating...' : 'Create Account'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <p className="auth-footer">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </div>
        </div>

        <div className="auth-right signup-right">
          <div className="benefits-list">
            <h2>{userType === 'creator' ? 'Why Join as a Creator?' : 'Why Join as a Brand?'}</h2>
            
            {/* Supported Platforms */}
            <div className="signup-platforms">
              <p className="platforms-label">Supported Platforms</p>
              <div className="platform-badges">
                <div className="platform-badge-item active">
                  <span className="platform-badge-icon instagram">📸</span>
                  <span>Instagram</span>
                  <span className="badge-status active">Active</span>
                </div>
                <div className="platform-badge-item coming-soon">
                  <span className="platform-badge-icon youtube">▶️</span>
                  <span>YouTube</span>
                  <span className="badge-status soon">Soon</span>
                </div>
                <div className="platform-badge-item coming-soon">
                  <span className="platform-badge-icon twitter">𝕏</span>
                  <span>Twitter</span>
                  <span className="badge-status soon">Soon</span>
                </div>
              </div>
            </div>

            {userType === 'creator' ? (
              <ul>
                {MILAN_WORKSPACE_ENABLED ? (
                  <>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Connect Your Instagram</strong>
                        <p>Link your Instagram account to showcase your stats</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Get Brand Collaborations</strong>
                        <p>Receive campaign invitations from verified brands</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Secure Payments</strong>
                        <p>Get paid safely with escrow-protected payments</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Performance Tracking</strong>
                        <p>Track your content performance with real-time analytics</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Free to Join</strong>
                        <p>No subscription fees, only small commission on earnings</p>
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Magic-link Instagram connect</strong>
                        <p>When a brand invites you via Performance Hub, connect Instagram in one click — no long signup</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>You stay in control</strong>
                        <p>Authorize what brands can measure; revoke anytime from Instagram</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>No platform escrow required</strong>
                        <p>Payments stay between you and the brand or agency you already work with</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Optional creator account</strong>
                        <p>Register if you want a Milan profile; Performance Hub invites work without one</p>
                      </div>
                    </li>
                  </>
                )}
              </ul>
            ) : (
              <ul>
                {MILAN_WORKSPACE_ENABLED ? (
                  <>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Instagram Creator Network</strong>
                        <p>Access verified Instagram creators for your campaigns</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>AI-Powered Matching</strong>
                        <p>Get creator recommendations based on your campaign needs</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Secure Escrow Payments</strong>
                        <p>Milestone-based payments with funds protection</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Performance Tracking</strong>
                        <p>Monitor campaign metrics with Instagram API integration</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Pay for Results</strong>
                        <p>No monthly subscriptions, only 3.5% platform fee on success</p>
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Performance Hub access</strong>
                        <p>Upload your roster CSV and track Instagram posts you already briefed</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Magic-link invites</strong>
                        <p>Creators connect Instagram once — they don’t need a Milan account</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Verified metrics &amp; economics</strong>
                        <p>Daily sync, top performers, and CPM/CPE-style views in one place</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Free tier to start</strong>
                        <p>Upgrade to Pro or Enterprise when your roster grows</p>
                      </div>
                    </li>
                    <li>
                      <span className="benefit-icon">✓</span>
                      <div>
                        <strong>Fits your agency flow</strong>
                        <p>Keep outreach and payments in your tools — Milan measures what ran</p>
                      </div>
                    </li>
                  </>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
